import React from 'react';

type buttonType =
  | 'primary-filled'
  | 'primary-outlined'
  | 'primary-dashed'
  | 'orange-filled'
  | 'orange-outlined'
  | 'red-filled'
  | 'red-outlined'
  | 'red-dashed'
  | 'skyblue-filled'
  | 'skyblue-outlined'
  | 'default';
interface HFbuttonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  type?: buttonType;
  height?: number;
  /** 픽셀 고정 너비. 'auto'를 주면 글자 길이에 맞춰 줄어든다. 없으면 w-full */
  width?: number | 'auto';
  isFocus?: boolean;
}

/**
 * 젭스 버튼
 * @description 젭스 공통 버튼 컴포넌트. 디자인 시스템에 맞는 버튼 컴포넌트를 제공합니다.
 */
const HFbutton = ({
  type = 'primary-filled',
  className,
  height = 50,
  width,
  isFocus,
  ...rest
}: HFbuttonProps) => {
  const btnRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (isFocus) {
      btnRef.current?.focus();
    }
  }, [isFocus]);

  const clickhandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    rest.onClick && rest.onClick(e);
  };
  /**
   * 색상 규칙
   *
   * 계열마다 기본색 하나를 정하고, filled / outlined가 같은 기본색을 공유한다.
   * (primary=purple5, orange=orange4, red=red5, skyblue=skyblue)
   *
   * - filled   : 배경 = 기본색, 글자 = 흰색.
   *              hover는 기본색을 한 단계, active는 두 단계 어둡게 한다.
   *              테두리는 항상 배경과 같은 색이라 상태가 바뀌어도 크기·윤곽이 흔들리지 않는다.
   * - outlined : 배경 = 흰색, 테두리·글자 = 기본색.
   *              테두리·글자는 그대로 두고 배경에만 기본색 5%(hover) → 10%(active) 틴트를 깐다.
   * - dashed   : 점선 테두리. 글자는 기본색이지만 테두리는 평소 회색(gray5)이라
   *              화면에서 튀지 않고, hover·active에서만 기본색 테두리와 틴트가 켜진다.
   *              보조 동작(추가하기·삭제하기)에 쓴다.
   * - disabled : 기본색을 흰색과 약 55% 섞은 흐린 톤을 쓴다.
   *              filled는 그 톤을 배경에(글자는 활성과 같은 흰색), outlined는 테두리·글자에 적용한다.
   *              같은 계열의 filled / outlined는 이 흐린 톤도 공유한다.
   * - default  : 브랜드색이 없는 중립 버튼. 틴트가 파랗게 비치지 않도록 gray 토큰
   *              (Open Color 계열이라 파랑기가 있다) 대신 무채색 회색 단계를 쓴다.
   *
   * 토큰이 없는 단계는 16진값을 그대로 적는다. (tailwind.config.js의 skyblue·orange 등은
   * 단일 색이라 `skyblue-600` 같은 음영 클래스가 생성되지 않는다.)
   */
  const getColor = (type: buttonType) => {
    switch (type) {
      case 'primary-filled':
      default:
        return rest.disabled
          ? 'bg-purple2 text-white'
          : 'bg-purple5 text-white border border-purple5 hover:bg-purple6 hover:border-purple6 active:bg-purple7 active:border-purple7';
      case 'primary-outlined':
        return rest.disabled
          ? 'bg-white text-purple2 border border-purple2'
          : 'bg-white text-purple5 border border-purple5 hover:bg-purple5/5 active:bg-purple5/10';
      case 'primary-dashed':
        return rest.disabled
          ? 'bg-white text-purple2 border border-dashed border-gray2'
          : 'bg-white text-purple5 border border-dashed border-gray5 hover:border-purple5 hover:bg-purple5/5 active:border-purple5 active:bg-purple5/10';
      case 'orange-filled':
        return rest.disabled
          ? 'bg-[#FFC9B0] text-white'
          : 'bg-orange4 text-white border border-orange4 hover:bg-orange5 hover:border-orange5 active:bg-[#D34100] active:border-[#D34100]';
      case 'orange-outlined':
        return rest.disabled
          ? 'bg-white text-[#FFC9B0] border border-[#FFC9B0]'
          : 'bg-white border border-orange4 text-orange4 hover:bg-orange4/5 active:bg-orange4/10';
      case 'red-filled':
        return rest.disabled
          ? 'bg-[#FFA5A9] text-white'
          : 'bg-red5 text-white border border-red5 hover:bg-[#E03037] hover:border-[#E03037] active:bg-[#C22A30] active:border-[#C22A30]';
      case 'red-outlined':
        return rest.disabled
          ? 'bg-white text-[#FFA5A9] border border-[#FFA5A9]'
          : 'bg-white border border-red5 text-red5 hover:bg-red5/5 active:bg-red5/10';
      case 'red-dashed':
        return rest.disabled
          ? 'bg-white text-[#FFA5A9] border border-dashed border-gray2'
          : 'bg-white text-red5 border border-dashed border-gray5 hover:border-red5 hover:bg-red5/5 active:border-red5 active:bg-red5/10';
      case 'skyblue-filled':
        return rest.disabled
          ? 'bg-[#9DC9FF] text-white'
          : 'bg-skyblue text-white border border-skyblue hover:bg-[#2176E0] hover:border-[#2176E0] active:bg-[#1D66C2] active:border-[#1D66C2]';
      case 'skyblue-outlined':
        return rest.disabled
          ? 'bg-white text-[#9DC9FF] border border-[#9DC9FF]'
          : 'bg-white border border-skyblue text-skyblue hover:bg-skyblue/5 active:bg-skyblue/10';
      case 'default':
        return rest.disabled
          ? 'bg-white text-gray4 border border-gray4'
          : 'bg-white text-gray9 border border-gray6 hover:bg-gray1 active:bg-gray2';
    }
  };

  const py = (height - 26) / 2;
  // 'auto'는 글자 길이만큼만 차지하도록 좌우 여백을 조금 더 준다.
  const widthStyle =
    width === 'auto'
      ? 'w-auto px-4'
      : width === undefined
        ? 'w-full px-2.5'
        : 'px-2.5';

  return (
    <button
      {...rest}
      ref={btnRef}
      // 숫자 너비는 임의값 클래스가 빌드 시점에 생성되지 않아 인라인으로 지정한다.
      style={
        typeof width === 'number'
          ? { width: `${width}px`, ...rest.style }
          : rest.style
      }
      onClick={clickhandler}
      className={`${widthStyle} h-auto py-[${py}px] box-border rounded font-bold ${
        className === undefined ? getColor(type) : className
      }`}
    />
  );
};

export default HFbutton;
